import React from "react";
import { Link } from "react-router-dom";
import EditorialHero from "../components/EditorialHero";
import EditorialCtaFlow from "../components/EditorialCtaFlow";
import CapabilityNavigator from "../components/CapabilityNavigator";
import SectionIntro from "../components/SectionIntro";
import LogoTicker from "../components/LogoTicker";
import ActionLink from "../components/ActionLink";
import organizationLogos from "../organizationLogos";
import capabilityAreas from "../content/capabilityAreas";
import { workTransformations } from "../content/work";

const transformationArtwork = {
  cooperators: {
    src: "/images/oak/OakBanner-800.webp",
    srcSet:
      "/images/oak/OakBanner-480.webp 480w, /images/oak/OakBanner-800.webp 800w",
    sizes: "(max-width: 760px) calc(100vw - 2rem), 400px",
    width: 1536,
    height: 1024,
    alt: "Oak Design System shown across digital product interfaces and component examples",
  },
  xe: {
    src: "/images/xe/XeCurrencyBanner-800.webp",
    srcSet:
      "/images/xe/XeCurrencyBanner-480.webp 480w, /images/xe/XeCurrencyBanner-800.webp 800w",
    sizes: "(max-width: 760px) calc(100vw - 2rem), 400px",
    width: 1536,
    height: 1024,
    alt: "XE Currency mobile platform shown with currency tools and financial data",
  },
};

const HomeTransformationCard = ({
  title,
  subtitle,
  eyebrow,
  description,
  image,
  to,
}) => (
  <article className="prototype-home__transformation-card">
    <Link
      aria-label={`Explore ${title}: ${subtitle}`}
      className="prototype-home__transformation-card-link"
      to={to}
    >
      <span className="prototype-home__transformation-card-copy">
        {eyebrow && (
          <span className="prototype-home__transformation-card-eyebrow">
            {eyebrow}
          </span>
        )}
        <strong>{title}</strong>
        {subtitle && <span>{subtitle}</span>}
        {description && <small>{description}</small>}
      </span>

      {image?.src && (
        <span className="prototype-home__transformation-card-media">
          <img
            alt={image.alt || ""}
            decoding="async"
            height={image.height}
            loading="lazy"
            sizes={image.sizes}
            src={image.src}
            srcSet={image.srcSet}
            width={image.width}
          />
        </span>
      )}

      <span
        className="prototype-home__transformation-card-arrow"
        aria-hidden="true"
      />
    </Link>
  </article>
);

const HomePage1 = () => (
  <div className="prototype-page prototype-home">
    <EditorialHero
      eyebrow={
        <>
          UX & PRODUCT DESIGN LEADER •
          <br />
          BOARD DIRECTOR • EDUCATOR • SPEAKER
        </>
      }
      title="Building organizational capability through design"
      description="From executive strategy to hands-on execution, I grow high-performing teams, establish scalable and inclusive design practices, align stakeholders around a shared direction, and build the governance and systems that enable better decisions and consistent execution as organizations evolve."
      actions={<ActionLink to="/work">Explore my work</ActionLink>}
      stats={[
        {
          value: "20+",
          label: "Years shaping digital products & organizations",
        },
        { value: "3M+", label: "Daily users across web platforms" },
        { value: "80M+", label: "Mobile app installs" },
      ]}
      media={{
        src: "/images/hero/magued-hanna-homepage-portrait-800.webp",
        srcSet:
          "/images/hero/magued-hanna-homepage-portrait-480.webp 480w, /images/hero/magued-hanna-homepage-portrait-800.webp 800w, /images/hero/magued-hanna-homepage-portrait-930.webp 930w",
        sizes: "(max-width: 768px) 88vw, 640px",
        width: 930,
        height: 1023,
        alt: "Magued Hanna",
        loading: "eager",
        decoding: "async",
        fetchPriority: "high",
      }}
      textWidth="medium"
      contentWidth="75%"
      mediaWidth="wide"
      mediaSide="right"
      mediaTopGap="none"
      mediaBleedRight
      mediaBleedBottom
      mediaInset="none"
      mediaFit="contain"
      mediaPosition="right-bottom"
      mediaTreatment={{
        background: "default",
        leftBlend: "standard",
        leftBlendWidth: "default",
        topBlend: "none",
        topBlendHeight: "default",
      }}
    />

    <section className="prototype-section prototype-home__capabilities">
      <div className="container">
        <CapabilityNavigator
          title="How I Create Value"
          items={capabilityAreas}
          defaultActiveId="strategy-alignment"
          defaultExpanded={false}
          evidenceMode="panel"
          evidenceLabel="Highlights"
          evidenceControlPlacement="inline"
          showArrows="auto"
          showDescription
          transition="fade"
          placement="section"
        />
      </div>
    </section>

    <section className="prototype-section prototype-home__transformations">
      <div className="container prototype-home__transformations-layout">
        <SectionIntro
          title="Featured Work"
          headingTag="h2"
        >
          <p>
            From product design to leading multidisciplinary UX teams across
            enterprise and consumer platforms, my experience spans fintech,
            insurance, real estate, automotive, and technology. Across these
            industries, I’ve translated business strategy into practical
            solutions that balance customer needs, business objectives, and
            technical realities. These two transformation stories best
            illustrate the breadth of that experience and the impact of my
            work.
          </p>
        </SectionIntro>

        <div
          className="prototype-home__transformation-cards"
          aria-label="Featured transformations"
        >
          {workTransformations.map((transformation) => (
            <HomeTransformationCard
              key={transformation.id}
              title={transformation.label}
              subtitle={transformation.tabDescription}
              image={transformationArtwork[transformation.id]}
              to={`/work/${transformation.id}`}
            />
          ))}
        </div>
      </div>
    </section>

    <section className="prototype-section prototype-home__leadership">
      <div className="container">
        <div className="home-leadership__layout">
          <div className="prototype-home__leadership-media">
            <img
              alt="Magued Hanna in conversation onstage at FITC Spotlight UX"
              decoding="async"
              height="791"
              loading="lazy"
              sizes="(max-width: 760px) calc(100vw - 2rem), 400px"
              src="/images/editorial/home-leadership-fitc-speaking-800.webp"
              srcSet="/images/editorial/home-leadership-fitc-speaking-480.webp 480w, /images/editorial/home-leadership-fitc-speaking-800.webp 800w"
              width="1534"
            />
          </div>
          <div className="home-leadership__content">
            <SectionIntro title="Leadership Approach" headingTag="h2">
              <EditorialCtaFlow
                action={
                  <ActionLink to="/leadership">Explore how I lead</ActionLink>
                }
              >
                <p>
                  Leadership is measured by what continues long after a product
                  ships. It includes building stronger teams, sharing knowledge,
                  contributing to the profession, and helping others grow through
                  board leadership, teaching, speaking, and mentoring. Across each
                  of these roles, the goal remains the same: leaving people,
                  organizations, and the profession stronger than I found them.
                </p>
              </EditorialCtaFlow>
            </SectionIntro>
          </div>
        </div>
      </div>
    </section>

    <LogoTicker
      items={organizationLogos}
      label="Organizations I’ve helped"
      showControl={false}
      showLabel={false}
    />
  </div>
);

export default HomePage1;
